import React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { PinLockGate } from "./PinLockGate";
import {
  selectSessionEndedReason,
  selectSessionStaticData,
} from "../../store/reducers/sessionSlice";
import { SessionStatus } from "../../types/session.definition";
import { getAuthUrl } from "../../utils/config-utils";

import type { RootState } from "../../store/store";
interface PrivateRouteProps {
  children: React.ReactNode;
  allowedRoles: ("patient" | "doctor" | "manager")[];
  requiresLicence?: boolean;
}

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  allowedRoles,
  requiresLicence = false,
}) => {
  const { sessionStatus } = useSelector(selectSessionStaticData);
  const { loading } = useSelector((state: RootState) => state.session);
  const endedReason = useSelector(selectSessionEndedReason);

  // While the session is still being validated, render nothing to avoid
  // briefly mounting unrelated routes (e.g. Home / FaqSection).
  if (loading) return null;

  // Ended for security reasons: SessionEndedNotice explains, then goes to login.
  if (endedReason) return null;

  if (sessionStatus !== SessionStatus.VALID_SESSION) {
    const language = localStorage.getItem("language") || "fr";
    const loginUrl = new URL(getAuthUrl());
    loginUrl.searchParams.set("language", language);
    loginUrl.searchParams.set(
      "returnUrl",
      window.location.pathname + window.location.search
    );
    window.location.href = loginUrl.toString();
    return null;
  }

  // The PIN lock comes first: while locked /session holds no metadata, so the
  // role and licence checks run once it is open (MED-1620).
  return (
    <PinLockGate>
      <RouteChecks
        allowedRoles={allowedRoles}
        requiresLicence={requiresLicence}
      >
        {children}
      </RouteChecks>
    </PinLockGate>
  );
};

const RouteChecks: React.FC<PrivateRouteProps> = ({
  children,
  allowedRoles,
  requiresLicence = false,
}) => {
  const { session } = useSelector((state: RootState) => state.session);

  if (
    !allowedRoles.includes(
      session?.user?.roleName?.toLowerCase() as "patient" | "doctor" | "manager"
    )
  ) {
    return <Navigate to="/" />;
  }

  if (requiresLicence && session?.metadata?.doctor?.licenceExpired !== false) {
    return <Navigate to="/doctor/profile" />;
  }

  return <>{children}</>;
};
